import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, applyActionCode, checkActionCode } from 'firebase/auth';
import { getDatabase, ref, update } from 'firebase/database';
import { app } from '../firebase';

const auth = getAuth(app);
const database = getDatabase(app);

const EmailVerificationHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const handleVerification = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      // Get userType from URL if provided (set during registration)
      const urlUserType = searchParams.get('userType');

      // Check if this is an email verification action
      if (mode !== 'verifyEmail') {
        setStatus('error');
        setMessage('Invalid verification link.');
        return;
      }

      if (!oobCode) {
        setStatus('error');
        setMessage('Verification code is missing.');
        return;
      }

      try {
        // First, check the action code to get user info
        const info = await checkActionCode(auth, oobCode);
        const userEmail = info?.data?.email;

        // DEBUG: Log verification info
        console.log('=== EMAIL VERIFICATION HANDLER DEBUG ===');
        console.log('Mode:', mode);
        console.log('OobCode:', oobCode ? 'present' : 'missing');
        console.log('URL userType:', urlUserType);
        console.log('User email from action code:', userEmail);

        // Apply the verification code
        await applyActionCode(auth, oobCode);
        console.log('applyActionCode succeeded');

        // The email has been verified in Firebase Auth
        setStatus('success');
        setMessage('Your email has been verified successfully!');

        // Determine user type - first from URL, then from database
        let determinedUserType = urlUserType;

        // Update the emailVerified status in the database
        const currentUser = auth.currentUser;
        console.log('Current user logged in:', !!currentUser);
        console.log('Current user UID:', currentUser?.uid);

        if (currentUser) {
          // User is logged in, update their database record by UID
          console.log('Updating by UID...');
          determinedUserType =
            await updateUserVerificationStatus(currentUser.uid) ||
            determinedUserType;
          console.log('Update by UID result:', determinedUserType);
        } else if (userEmail) {
          // User is not logged in, find and update by email
          console.log('Updating by email:', userEmail);
          determinedUserType =
            await updateVerificationByEmail(userEmail) ||
            determinedUserType;
          console.log('Update by email result:', determinedUserType);
        } else {
          console.log('WARNING: No currentUser and no userEmail!');
        }
        console.log('Final determinedUserType:', determinedUserType);
        console.log('=========================================');

        // Set the user type for display
        if (determinedUserType) {
          setUserType(determinedUserType);
        }

        // Redirect to login after 3 seconds
        setTimeout(() => {
          // Determine which login page to redirect to based on user type
          if (determinedUserType === 'employer') {
            navigate('/employer-sign-in', {
              state: { verified: true, message: 'Email verified! You can now sign in.' }
            });
          } else if (determinedUserType === 'candidate') {
            navigate('/candidate-sign-in', {
              state: { verified: true, message: 'Email verified! You can now sign in.' }
            });
          } else {
            // Default to candidate sign-in if we couldn't determine the type
            navigate('/candidate-sign-in', {
              state: { verified: true, message: 'Email verified! You can now sign in.' }
            });
          }
        }, 3000);

      } catch (error) {
        console.error('Error verifying email:', error);

        let errorMessage = 'Failed to verify email.';

        if (error.code === 'auth/expired-action-code') {
          errorMessage = 'This verification link has expired. Please request a new one.';
        } else if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'This verification link is invalid or has already been used.';
        } else if (error.code === 'auth/user-disabled') {
          errorMessage = 'This account has been disabled.';
        } else if (error.code === 'auth/user-not-found') {
          errorMessage = 'No account found for this verification link.';
        }

        setStatus('error');
        setMessage(errorMessage);

        // Redirect to home page after 5 seconds
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    };

    const updateUserVerificationStatus = async (uid) => {
      try {
        const { get } = await import('firebase/database');

        // Check if user is in employers collection
        const employerRef = ref(database, `employers/${uid}`);
        const employerSnapshot = await get(employerRef);

        if (employerSnapshot.exists()) {
          await update(employerRef, {
            emailVerified: true,
            verifiedAt: new Date().toISOString()
          });
          return 'employer';
        }

        // Check if user is in candidates collection
        const candidateRef = ref(database, `candidates/${uid}`);
        const candidateSnapshot = await get(candidateRef);

        if (candidateSnapshot.exists()) {
          await update(candidateRef, {
            emailVerified: true,
            verifiedAt: new Date().toISOString()
          });
          return 'candidate';
        }

        return null;
      } catch (error) {
        console.error('Error updating verification status:', error);
        return null;
      }
    };

    // Function to find and update user by email when not logged in
    const updateVerificationByEmail = async (email) => {
      console.log('=== updateVerificationByEmail DEBUG ===');
      console.log('Searching for email:', email);
      try {
        const { get, query, orderByChild, equalTo } =
          await import('firebase/database');

        // Search in employers collection
        console.log('Searching employers collection...');
        const employersRef = ref(database, 'employers');
        const employerQuery = query(
          employersRef,
          orderByChild('email'),
          equalTo(email)
        );
        const employerSnapshot = await get(employerQuery);
        console.log('Employer query result exists:', employerSnapshot.exists());

        if (employerSnapshot.exists()) {
          const employerData = employerSnapshot.val();
          const uid = Object.keys(employerData)[0];
          console.log('Found employer with UID:', uid);
          await update(ref(database, `employers/${uid}`), {
            emailVerified: true,
            verifiedAt: new Date().toISOString()
          });
          console.log('Updated employer emailVerified to true');
          return 'employer';
        }

        // Search in candidates collection
        console.log('Searching candidates collection...');
        const candidatesRef = ref(database, 'candidates');
        const candidateQuery = query(
          candidatesRef,
          orderByChild('email'),
          equalTo(email)
        );
        const candidateSnapshot = await get(candidateQuery);
        console.log('Candidate query result exists:', candidateSnapshot.exists());

        if (candidateSnapshot.exists()) {
          const candidateData = candidateSnapshot.val();
          const uid = Object.keys(candidateData)[0];
          console.log('Found candidate with UID:', uid);
          console.log('Candidate data before update:', candidateData[uid]);
          await update(ref(database, `candidates/${uid}`), {
            emailVerified: true,
            verifiedAt: new Date().toISOString()
          });
          console.log('Updated candidate emailVerified to true');
          console.log('=========================================');
          return 'candidate';
        }

        console.log('No user found with this email in either collection');
        console.log('=========================================');
        return null;
      } catch (error) {
        console.error('Error updating verification by email:', error);
        console.log('=========================================');
        return null;
      }
    };

    handleVerification();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">Verifying your email...</h2>
              <p className="mt-2 text-sm text-gray-600">Please wait while we verify your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">Email Verified!</h2>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
              <p className="mt-4 text-sm text-gray-500">Redirecting you to the login page...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg className="h-8 w-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">Verification Failed</h2>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
              <p className="mt-4 text-sm text-gray-500">Redirecting you to the home page...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationHandler;
